import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Play, Clock, BookOpen, Edit, Trash2, FileText, Video, HelpCircle, ChevronDown, ChevronRight, Users, CheckCircle, ClipboardList, UserPlus, ChevronUp, Upload, GripVertical, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../components/RichTextEditor';
import LessonBlockEditor from '../components/LessonBlockEditor';
import LessonBlockRenderer from '../components/LessonBlockRenderer';
import CoursePreinscriptions from '../components/CoursePreinscriptions';
import AnnouncementBoard from '../components/AnnouncementBoard';

// Drag & Drop imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sortable Module Component
const SortableModule = ({ module, children, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white">
      <div className="flex items-center">
        {!disabled && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-2 hover:bg-slate-100 rounded touch-none flex-shrink-0"
            title="Arrastra para reordenar"
          >
            <GripVertical className="w-5 h-5 text-slate-400" />
          </div>
        )}
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

// Sortable Lesson Component
const SortableLesson = ({ lesson, children, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lesson.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center">
        {!disabled && (
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 rounded touch-none flex-shrink-0 ml-2"
            title="Arrastra para reordenar"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </div>
        )}
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

const OnlineCourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [quizzes, setQuizzes] = useState({});
  const [lessonProgress, setLessonProgress] = useState({}); // Track completed lessons
  
  // Dialogs
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [showLessonDialog, setShowLessonDialog] = useState(false);
  const [showLessonPreview, setShowLessonPreview] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  
  // Form data
  const [moduleForm, setModuleForm] = useState({ nombre: '', descripcion: '', orden: 0 });
  const [lessonForm, setLessonForm] = useState({
    nombre: '', tipo: 'text', contenido: '', video_url: '', documento_url: '', documento_nombre: '', bloques: [], duracion_estimada: 10, orden: 0
  });
  const [quizForm, setQuizForm] = useState({
    nombre: '', descripcion: '', tiempo_limite: null, intentos_permitidos: 1, puntuacion_minima: 60, mostrar_respuestas: true
  });
  
  // Estados para inscripción de alumnos
  const [allUsers, setAllUsers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  
  // Estados para tutores
  const [instructors, setInstructors] = useState([]);
  const [showTutorDialog, setShowTutorDialog] = useState(false);
  const [selectedTutorId, setSelectedTutorId] = useState('');

  const isAdmin = user?.role === 'Administrador' || user?.role === 'Formador';
  const isStudent = user?.role === 'Alumno';
  const isTutor = course?.is_tutor || false;
  const canEdit = isAdmin || isTutor;

  useEffect(() => {
    fetchCourse();
  }, [courseId]);
  
  useEffect(() => {
    if (isAdmin && courseId) {
      fetchUsersAndEnrollments();
      fetchInstructors();
    }
  }, [isAdmin, courseId]);
  
  const fetchInstructors = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/instructors`);
      setInstructors(response.data);
    } catch (error) {
      console.error('Error fetching instructors:', error);
    }
  };
  
  const fetchUsersAndEnrollments = async () => {
    try {
      const [studentsRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API_URL}/users/students`),
        axios.get(`${API_URL}/online-courses/${courseId}/enrollments`)
      ]);
      setAllUsers(studentsRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      console.error('Error fetching users/enrollments:', error);
    }
  };

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/online-courses/${courseId}`);
      setCourse(response.data);
      // Expandir todos los módulos por defecto
      const expanded = {};
      (response.data.modules || []).forEach(m => expanded[m.id] = true);
      setExpandedModules(expanded);
      
      // Obtener quizzes de las lecciones tipo quiz
      const quizMap = {};
      for (const module of response.data.modules || []) {
        for (const lesson of module.lessons || []) {
          if (lesson.tipo === 'quiz') {
            try {
              const quizRes = await axios.get(`${API_URL}/quizzes?lesson_id=${lesson.id}`);
              if (quizRes.data.length > 0) {
                quizMap[lesson.id] = quizRes.data[0];
              }
            } catch (e) {
              // Quiz not found for this lesson, skip
            }
          }
        }
      }
      setQuizzes(quizMap);
      
      // Cargar progreso del estudiante (si está inscrito)
      if (response.data.enrollment) {
        try {
          const progressRes = await axios.get(`${API_URL}/online-courses/${courseId}/my-progress`);
          setLessonProgress(progressRes.data);
        } catch (e) {
          // No progress yet, that's ok
        }
      }
    } catch (error) {
      toast.error('Error al cargar el curso');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    try {
      await axios.post(`${API_URL}/online-enrollments`, {
        online_course_id: courseId,
        student_id: user.id
      });
      toast.success('¡Te has inscrito al curso!');
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inscribirse');
    }
  };
  
  // Inscribir alumno (admin)
  const handleEnrollStudent = async () => {
    if (!selectedStudentId) {
      toast.error('Selecciona un alumno');
      return;
    }
    try {
      await axios.post(`${API_URL}/online-enrollments`, {
        online_course_id: courseId,
        student_id: selectedStudentId
      });
      toast.success('Alumno inscrito correctamente');
      setShowEnrollDialog(false);
      setSelectedStudentId('');
      fetchUsersAndEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inscribir alumno');
    }
  };
  
  // Desinscribir alumno (admin)
  const handleUnenrollStudent = async (studentId) => {
    if (!window.confirm('¿Eliminar este alumno del curso?')) return;
    try {
      await axios.delete(`${API_URL}/online-courses/${courseId}/enrollments/${studentId}`);
      toast.success('Alumno eliminado del curso');
      fetchUsersAndEnrollments();
    } catch (error) {
      toast.error('Error al eliminar alumno');
    }
  };
  
  // Añadir tutor
  const handleAddTutor = async () => {
    if (!selectedTutorId) {
      toast.error('Selecciona un instructor');
      return;
    }
    try {
      await axios.post(`${API_URL}/online-courses/${courseId}/tutors?tutor_id=${selectedTutorId}`);
      toast.success('Tutor añadido correctamente');
      setShowTutorDialog(false);
      setSelectedTutorId('');
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir tutor');
    }
  };
  
  // Eliminar tutor
  const handleRemoveTutor = async (tutorId) => {
    if (!window.confirm('¿Eliminar este tutor del curso?')) return;
    try {
      await axios.delete(`${API_URL}/online-courses/${courseId}/tutors/${tutorId}`);
      toast.success('Tutor eliminado del curso');
      fetchCourse();
    } catch (error) {
      toast.error('Error al eliminar tutor');
    }
  };

  // Reordenar módulo
  const handleReorderModule = async (moduleId, direction) => {
    try {
      await axios.put(`${API_URL}/modules/${moduleId}/reorder?direction=${direction}`);
      toast.success('Módulo reordenado');
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reordenar módulo');
    }
  };

  // Reordenar lección
  const handleReorderLesson = async (lessonId, direction) => {
    try {
      await axios.put(`${API_URL}/lessons/${lessonId}/reorder?direction=${direction}`);
      toast.success('Lección reordenada');
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reordenar lección');
    }
  };

  // Subir documento
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLessonForm({
        ...lessonForm,
        documento_url: response.data.url,
        documento_nombre: response.data.original_name
      });
      toast.success('Documento subido correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    }
  };

  // Drag & Drop Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drag & Drop for Modules
  const handleModuleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const modules = course.modules || [];
    const oldIndex = modules.findIndex(m => m.id === active.id);
    const newIndex = modules.findIndex(m => m.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Update local state immediately for smooth UX
    const newModules = arrayMove(modules, oldIndex, newIndex);
    setCourse({ ...course, modules: newModules });
    
    // Update backend with new order
    try {
      const items = newModules.map((m, index) => ({ id: m.id, orden: index + 1 }));
      await axios.put(`${API_URL}/modules/batch-reorder?course_id=${courseId}`, { items });
      toast.success('Módulos reordenados');
    } catch (error) {
      // Revert on error
      fetchCourse();
      toast.error('Error al reordenar módulos');
    }
  };

  // Drag & Drop for Lessons within a Module
  const handleLessonDragEnd = async (moduleId, event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const module = course.modules?.find(m => m.id === moduleId);
    if (!module) return;
    
    const lessons = module.lessons || [];
    const oldIndex = lessons.findIndex(l => l.id === active.id);
    const newIndex = lessons.findIndex(l => l.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Update local state immediately
    const newLessons = arrayMove(lessons, oldIndex, newIndex);
    const newModules = course.modules.map(m => 
      m.id === moduleId ? { ...m, lessons: newLessons } : m
    );
    setCourse({ ...course, modules: newModules });
    
    // Update backend
    try {
      const items = newLessons.map((l, index) => ({ id: l.id, orden: index + 1 }));
      await axios.put(`${API_URL}/lessons/batch-reorder?module_id=${moduleId}`, { items });
      toast.success('Lecciones reordenadas');
    } catch (error) {
      fetchCourse();
      toast.error('Error al reordenar lecciones');
    }
  };

  // Quiz CRUD
  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    try {
      const quizData = {
        nombre: quizForm.nombre,
        descripcion: quizForm.descripcion,
        lesson_id: selectedLessonId,
        tiempo_limite: quizForm.tiempo_limite || null,
        intentos_permitidos: quizForm.intentos_permitidos || 1,
        puntuacion_minima: quizForm.puntuacion_minima || 60,
        mostrar_respuestas: quizForm.mostrar_respuestas
      };
      const response = await axios.post(`${API_URL}/quizzes`, quizData);
      toast.success('Quiz creado correctamente');
      setShowQuizDialog(false);
      setQuizForm({ nombre: '', descripcion: '', tiempo_limite: null, intentos_permitidos: 1, puntuacion_minima: 60, mostrar_respuestas: true });
      // Ir al editor del quiz
      navigate(`/quiz-editor/${response.data.id}`);
    } catch (error) {
      console.error('Error al crear quiz:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el quiz');
    }
  };

  const openQuizDialog = (lessonId) => {
    setSelectedLessonId(lessonId);
    const lesson = course.modules?.flatMap(m => m.lessons || []).find(l => l.id === lessonId);
    setQuizForm({
      nombre: `Evaluación: ${lesson?.nombre || 'Quiz'}`,
      descripcion: '',
      tiempo_limite: null,
      intentos_permitidos: 1,
      puntuacion_minima: 60,
      mostrar_respuestas: true
    });
    setShowQuizDialog(true);
  };

  // Module CRUD
  const handleSaveModule = async (e) => {
    e.preventDefault();
    try {
      if (editingModule) {
        await axios.put(`${API_URL}/modules/${editingModule.id}`, {
          ...moduleForm,
          online_course_id: courseId
        });
        toast.success('Módulo actualizado');
      } else {
        await axios.post(`${API_URL}/modules`, {
          ...moduleForm,
          online_course_id: courseId,
          orden: (course.modules?.length || 0) + 1
        });
        toast.success('Módulo creado');
      }
      setShowModuleDialog(false);
      setEditingModule(null);
      setModuleForm({ nombre: '', descripcion: '', orden: 0 });
      fetchCourse();
    } catch (error) {
      toast.error('Error al guardar el módulo');
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('¿Eliminar este módulo y todas sus lecciones?')) return;
    try {
      await axios.delete(`${API_URL}/modules/${moduleId}`);
      toast.success('Módulo eliminado');
      fetchCourse();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Lesson CRUD
  const handleSaveLesson = async (e) => {
    e.preventDefault();
    try {
      if (editingLesson) {
        await axios.put(`${API_URL}/lessons/${editingLesson.id}`, {
          ...lessonForm,
          module_id: selectedModuleId
        });
        toast.success('Lección actualizada');
      } else {
        const module = course.modules.find(m => m.id === selectedModuleId);
        await axios.post(`${API_URL}/lessons`, {
          ...lessonForm,
          module_id: selectedModuleId,
          orden: (module?.lessons?.length || 0) + 1
        });
        toast.success('Lección creada');
      }
      setShowLessonDialog(false);
      setEditingLesson(null);
      setSelectedModuleId(null);
      setLessonForm({ nombre: '', tipo: 'text', contenido: '', video_url: '', documento_url: '', documento_nombre: '', bloques: [], duracion_estimada: 10, orden: 0 });
      fetchCourse();
    } catch (error) {
      toast.error('Error al guardar la lección');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('¿Eliminar esta lección?')) return;
    try {
      await axios.delete(`${API_URL}/lessons/${lessonId}`);
      toast.success('Lección eliminada');
      fetchCourse();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openLessonDialog = (moduleId, lesson = null) => {
    setSelectedModuleId(moduleId);
    if (lesson) {
      setEditingLesson(lesson);
      setLessonForm({
        nombre: lesson.nombre,
        tipo: lesson.tipo,
        contenido: lesson.contenido || '',
        video_url: lesson.video_url || '',
        documento_url: lesson.documento_url || '',
        documento_nombre: lesson.documento_nombre || '',
        bloques: lesson.bloques || [],
        duracion_estimada: lesson.duracion_estimada || 10,
        orden: lesson.orden || 0
      });
    }
    setShowLessonDialog(true);
  };

  const getLessonIcon = (tipo) => {
    switch (tipo) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'document': return <FileText className="w-4 h-4" />;
      case 'quiz': return <HelpCircle className="w-4 h-4" />;
      case 'blocks': return <Layers className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Curso no encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/online-courses')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
        </div>

        {/* Course Info */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="h-48 bg-gradient-to-br from-[#004E89] to-[#0EA5E9] relative">
            {course.imagen_url && (
              <img 
                src={course.imagen_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${course.imagen_url}` : course.imagen_url} 
                alt={course.nombre} 
                className="w-full h-full object-cover" 
              />
            )}
            <div className="absolute inset-0 bg-black/30"></div>
            <div className="absolute bottom-4 left-6 right-6 text-white">
              <h1 className="font-manrope font-bold text-3xl mb-2">{course.nombre}</h1>
              {course.titulo && <p className="text-white/90">{course.titulo}</p>}
            </div>
          </div>
          <div className="p-6">
            <p className="text-slate-600 mb-4">{course.descripcion}</p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {Math.floor(course.duracion_estimada / 60)}h {course.duracion_estimada % 60}m
              </div>
              <div className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                {course.modules?.length || 0} módulos
              </div>
              <div className="flex items-center gap-1">
                <Play className="w-4 h-4" />
                {course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0} lecciones
              </div>
            </div>

            {/* Tutores del curso */}
            {(course.tutors?.length > 0 || isAdmin) && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Tutores del Curso ({course.tutors?.length || 0})
                  </h3>
                  {isAdmin && (
                    <Button size="sm" onClick={() => setShowTutorDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                      <UserPlus className="w-4 h-4 mr-1" />
                      Añadir Tutor
                    </Button>
                  )}
                </div>
                {(course.tutors?.length || 0) === 0 ? (
                  <p className="text-sm text-slate-500">No hay tutores asignados a este curso.</p>
                ) : (
                  <div className="space-y-2">
                    {course.tutors.map((tutor) => (
                      <div key={tutor.id} className="flex items-center justify-between bg-white p-2 rounded border">
                        <div>
                          <p className="text-sm font-medium">{tutor.nombre} {tutor.apellidos}</p>
                          <p className="text-xs text-slate-500">{tutor.email}</p>
                        </div>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveTutor(tutor.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Enrollment for students */}
            {isStudent && !course.enrollment && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-700 mb-3">¿Quieres empezar este curso?</p>
                <Button onClick={handleEnroll} className="bg-[#004E89] hover:bg-[#003B66]">
                  Inscribirme al curso
                </Button>
              </div>
            )}

            {/* Progress for enrolled students */}
            {course.enrollment && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-800">Tu progreso</span>
                  <span className="text-sm font-bold text-green-800">{course.enrollment.progreso}%</span>
                </div>
                <Progress value={course.enrollment.progreso} className="h-2" />
              </div>
            )}
            
            {/* Admin: Preinscripciones */}
            {isAdmin && (
              <div className="mt-6">
                <CoursePreinscriptions courseId={courseId} courseType="online" onApproved={fetchCourse} />
              </div>
            )}

            {/* Admin: Gestión de Alumnos Inscritos */}
            {isAdmin && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Alumnos Inscritos ({enrollments.length})
                  </h3>
                  <Button size="sm" onClick={() => setShowEnrollDialog(true)} className="bg-[#004E89] hover:bg-[#003B66]">
                    <UserPlus className="w-4 h-4 mr-1" />
                    Añadir Alumno
                  </Button>
                </div>
                {enrollments.length === 0 ? (
                  <p className="text-sm text-slate-500">No hay alumnos inscritos aún.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {enrollments.map((enrollment) => (
                      <div key={enrollment.id} className="flex items-center justify-between bg-white p-2 rounded border">
                        <div>
                          <p className="text-sm font-medium">{enrollment.student?.nombre} {enrollment.student?.apellidos}</p>
                          <p className="text-xs text-slate-500">{enrollment.student?.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{enrollment.progreso || 0}%</span>
                          <Button variant="ghost" size="sm" onClick={() => handleUnenrollStudent(enrollment.student_id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Forum and Chat buttons */}
            {(course.enrollment || isAdmin) && (
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate(`/forums/course/${courseId}`)}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                  Foro del Curso
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate(`/chat/course/${courseId}`)}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Chat en Vivo
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Announcements Section - visible for enrolled students, tutors, and admins */}
        {(course.enrollment || isAdmin || course.is_tutor) && (
          <AnnouncementBoard 
            courseId={courseId} 
            canPost={isAdmin || course.is_tutor} 
          />
        )}

        {/* Modules Section */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-manrope font-semibold text-xl">Contenido del curso</h2>
            {canEdit && (
              <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#004E89] hover:bg-[#003B66]">
                    <Plus className="w-4 h-4 mr-2" /> Añadir módulo
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingModule ? 'Editar Módulo' : 'Nuevo Módulo'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveModule} className="space-y-4">
                    <div>
                      <Label>Nombre del módulo *</Label>
                      <Input
                        value={moduleForm.nombre}
                        onChange={(e) => setModuleForm({ ...moduleForm, nombre: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Descripción</Label>
                      <Textarea
                        value={moduleForm.descripcion}
                        onChange={(e) => setModuleForm({ ...moduleForm, descripcion: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setShowModuleDialog(false)}>Cancelar</Button>
                      <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]">Guardar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="divide-y divide-slate-100">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleModuleDragEnd}
            >
              <SortableContext
                items={(course.modules || []).map(m => m.id)}
                strategy={verticalListSortingStrategy}
              >
                {(course.modules || []).map((module, moduleIndex) => (
                  <SortableModule key={module.id} module={module} disabled={!canEdit}>
                    <div className="bg-white">
                      {/* Module Header */}
                      <div
                        className="px-4 py-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedModules(prev => ({ ...prev, [module.id]: !prev[module.id] }))}
                      >
                        {expandedModules[module.id] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                        <div className="flex-1">
                          <h3 className="font-medium text-slate-900">Módulo {moduleIndex + 1}: {module.nombre}</h3>
                          <p className="text-sm text-slate-500">{module.lessons?.length || 0} lecciones</p>
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {/* Botones de reordenar módulo */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleReorderModule(module.id, 'up')}
                              disabled={moduleIndex === 0}
                              className="text-slate-500 hover:text-slate-700"
                              title="Subir módulo"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleReorderModule(module.id, 'down')}
                              disabled={moduleIndex === (course.modules?.length || 1) - 1}
                              className="text-slate-500 hover:text-slate-700"
                              title="Bajar módulo"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                            <div className="w-px h-4 bg-slate-200 mx-1"></div>
                            <Button variant="ghost" size="sm" onClick={() => openLessonDialog(module.id)}>
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingModule(module);
                              setModuleForm({ nombre: module.nombre, descripcion: module.descripcion || '', orden: module.orden });
                              setShowModuleDialog(true);
                            }}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteModule(module.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Lessons with Drag & Drop */}
                      {expandedModules[module.id] && (
                        <div className="bg-slate-50 border-t border-slate-100">
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleLessonDragEnd(module.id, event)}
                          >
                            <SortableContext
                              items={(module.lessons || []).map(l => l.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {(module.lessons || []).map((lesson, lessonIndex) => (
                                <SortableLesson key={lesson.id} lesson={lesson} disabled={!canEdit}>
                                  <div className="px-4 py-3 pl-10 flex items-center gap-4 hover:bg-slate-100 border-b border-slate-100 last:border-b-0">
                        {/* Lesson icon with completion status */}
                        {(() => {
                          // Check if lesson/quiz is completed
                          const isLessonCompleted = lessonProgress?.lessons?.[lesson.id]?.completed;
                          const quizId = quizzes[lesson.id]?.id;
                          const isQuizPassed = quizId && lessonProgress?.quizzes?.[quizId]?.passed;
                          const isCompleted = lesson.tipo === 'quiz' ? isQuizPassed : isLessonCompleted;
                          
                          return (
                            <div 
                              className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
                                isCompleted 
                                  ? 'bg-green-500 text-white border-0' 
                                  : 'bg-white border border-slate-200'
                              }`}
                              onClick={() => {
                                if (lesson.tipo === 'quiz' && quizzes[lesson.id]) {
                                  if (canEdit) {
                                    navigate(`/quiz-editor/${quizzes[lesson.id].id}`);
                                  } else {
                                    navigate(`/quiz/${quizzes[lesson.id].id}`);
                                  }
                                } else if (lesson.tipo !== 'quiz') {
                                  navigate(`/lessons/${lesson.id}`);
                                }
                              }}
                            >
                              {isCompleted ? (
                                <CheckCircle className="w-5 h-5" />
                              ) : (
                                getLessonIcon(lesson.tipo)
                              )}
                            </div>
                          );
                        })()}
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            if (lesson.tipo === 'quiz' && quizzes[lesson.id]) {
                              if (canEdit) {
                                navigate(`/quiz-editor/${quizzes[lesson.id].id}`);
                              } else {
                                navigate(`/quiz/${quizzes[lesson.id].id}`);
                              }
                            } else if (lesson.tipo !== 'quiz') {
                              navigate(`/lessons/${lesson.id}`);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">{lesson.nombre}</p>
                            {/* Completed badge */}
                            {(() => {
                              const isLessonCompleted = lessonProgress?.lessons?.[lesson.id]?.completed;
                              const quizId = quizzes[lesson.id]?.id;
                              const isQuizPassed = quizId && lessonProgress?.quizzes?.[quizId]?.passed;
                              const isCompleted = lesson.tipo === 'quiz' ? isQuizPassed : isLessonCompleted;
                              
                              if (isCompleted) {
                                return (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                    Completado
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          <p className="text-xs text-slate-500">
                            {lesson.tipo === 'quiz' 
                              ? (quizzes[lesson.id] ? `${quizzes[lesson.id].questions_count || 0} preguntas` : 'Quiz sin configurar')
                              : `${lesson.duracion_estimada} min`
                            }
                          </p>
                        </div>
                        {/* Quiz actions */}
                        {lesson.tipo === 'quiz' && (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            {quizzes[lesson.id] ? (
                              <>
                                {canEdit && (
                                  <>
                                    <Button variant="ghost" size="sm" onClick={() => navigate(`/quiz-editor/${quizzes[lesson.id].id}`)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => navigate(`/quiz-attempts/${quizzes[lesson.id].id}`)}>
                                      <Users className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {isStudent && (
                                  <Button size="sm" className="bg-[#004E89] hover:bg-[#003B66]" onClick={() => navigate(`/quiz/${quizzes[lesson.id].id}`)}>
                                    <Play className="w-4 h-4 mr-1" /> Iniciar
                                  </Button>
                                )}
                              </>
                            ) : (
                              canEdit && (
                                <Button size="sm" variant="outline" onClick={() => openQuizDialog(lesson.id)}>
                                  <Plus className="w-4 h-4 mr-1" /> Crear Quiz
                                </Button>
                              )
                            )}
                          </div>
                        )}
                        {canEdit && lesson.tipo !== 'quiz' && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {/* Botones de reordenar lección */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleReorderLesson(lesson.id, 'up')}
                              disabled={lessonIndex === 0}
                              className="text-slate-400 hover:text-slate-600"
                              title="Subir lección"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleReorderLesson(lesson.id, 'down')}
                              disabled={lessonIndex === (module.lessons?.length || 1) - 1}
                              className="text-slate-400 hover:text-slate-600"
                              title="Bajar lección"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                            <div className="w-px h-4 bg-slate-200 mx-1"></div>
                            <Button variant="ghost" size="sm" onClick={() => openLessonDialog(module.id, lesson)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteLesson(lesson.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {canEdit && lesson.tipo === 'quiz' && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {/* Botones de reordenar quiz */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleReorderLesson(lesson.id, 'up')}
                              disabled={lessonIndex === 0}
                              className="text-slate-400 hover:text-slate-600"
                              title="Subir quiz"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleReorderLesson(lesson.id, 'down')}
                              disabled={lessonIndex === (module.lessons?.length || 1) - 1}
                              className="text-slate-400 hover:text-slate-600"
                              title="Bajar quiz"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                            <div className="w-px h-4 bg-slate-200 mx-1"></div>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteLesson(lesson.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                                  </div>
                                </SortableLesson>
                              ))}
                            </SortableContext>
                          </DndContext>
                          {(module.lessons || []).length === 0 && (
                            <div className="px-6 py-4 pl-14 text-sm text-slate-500">
                              No hay lecciones en este módulo
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </SortableModule>
                ))}
              </SortableContext>
            </DndContext>

            {(course.modules || []).length === 0 && (
              <div className="px-6 py-12 text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay módulos en este curso</p>
                {canEdit && <p className="text-sm text-slate-400 mt-1">Añade el primer módulo para comenzar</p>}
              </div>
            )}
          </div>
        </div>

        {/* Lesson Dialog */}
        <Dialog open={showLessonDialog} onOpenChange={(open) => {
          setShowLessonDialog(open);
          if (!open) {
            setEditingLesson(null);
            setLessonForm({ nombre: '', tipo: 'text', contenido: '', video_url: '', documento_url: '', documento_nombre: '', bloques: [], duracion_estimada: 10, orden: 0 });
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingLesson ? 'Editar Lección' : 'Nueva Lección'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveLesson} className="space-y-4">
              <div>
                <Label>Nombre de la lección *</Label>
                <Input
                  value={lessonForm.nombre}
                  onChange={(e) => setLessonForm({ ...lessonForm, nombre: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de contenido</Label>
                  <Select value={lessonForm.tipo} onValueChange={(v) => setLessonForm({ ...lessonForm, tipo: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blocks">Tema interactivo (bloques)</SelectItem>
                      <SelectItem value="text">Texto</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="document">Documento</SelectItem>
                      <SelectItem value="quiz">Quiz</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duración estimada (min)</Label>
                  <Input
                    type="number"
                    value={lessonForm.duracion_estimada}
                    onChange={(e) => setLessonForm({ ...lessonForm, duracion_estimada: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              {lessonForm.tipo === 'blocks' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Contenido del tema</Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowLessonPreview(true)} disabled={!(lessonForm.bloques || []).length} data-testid="lesson-preview-btn">
                      <Play className="w-4 h-4 mr-1" /> Vista previa
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">Apila bloques de texto, imágenes, vídeos, recuadros destacados y preguntas, como en COSY.</p>
                  <LessonBlockEditor
                    blocks={lessonForm.bloques || []}
                    onChange={(bloques) => setLessonForm({ ...lessonForm, bloques })}
                  />
                </div>
              )}
              {lessonForm.tipo === 'text' && (
                <div>
                  <Label>Contenido</Label>
                  <RichTextEditor
                    value={lessonForm.contenido}
                    onChange={(content) => setLessonForm({ ...lessonForm, contenido: content })}
                    placeholder="Escribe el contenido de la lección..."
                  />
                </div>
              )}
              {lessonForm.tipo === 'video' && (
                <div>
                  <Label>URL del video (YouTube/Vimeo)</Label>
                  <Input
                    value={lessonForm.video_url}
                    onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              )}
              {lessonForm.tipo === 'document' && (
                <div className="space-y-3">
                  <Label>Documento</Label>
                  <div className="flex flex-col gap-3">
                    {/* Opción de subir archivo */}
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        id="document-upload"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        onChange={handleDocumentUpload}
                      />
                      <label
                        htmlFor="document-upload"
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        <Upload className="w-8 h-8 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          Haz clic para subir un documento
                        </span>
                        <span className="text-xs text-slate-400">
                          PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT (máx. 20MB)
                        </span>
                      </label>
                    </div>
                    
                    {/* Mostrar archivo subido */}
                    {lessonForm.documento_url && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700 flex-1 truncate">
                          {lessonForm.documento_nombre || 'Documento subido'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setLessonForm({ ...lessonForm, documento_url: '', documento_nombre: '' })}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Separador */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-slate-200"></div>
                      <span className="text-xs text-slate-400">o usa una URL externa</span>
                      <div className="flex-1 h-px bg-slate-200"></div>
                    </div>
                    
                    {/* Opción de URL */}
                    <Input
                      value={lessonForm.documento_url?.startsWith('/api') ? '' : (lessonForm.documento_url || '')}
                      onChange={(e) => setLessonForm({ ...lessonForm, documento_url: e.target.value, documento_nombre: '' })}
                      placeholder="https://..."
                      disabled={lessonForm.documento_url?.startsWith('/api')}
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowLessonDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]">Guardar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Vista previa del tema (como lo verá el alumno) */}
        <Dialog open={showLessonPreview} onOpenChange={setShowLessonPreview}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lesson-preview-dialog">
            <DialogHeader>
              <DialogTitle>Vista previa del tema{lessonForm.nombre ? `: ${lessonForm.nombre}` : ''}</DialogTitle>
            </DialogHeader>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              {(lessonForm.bloques || []).length > 0 ? (
                <LessonBlockRenderer blocks={lessonForm.bloques} />
              ) : (
                <p className="text-center text-slate-400 py-8">Añade bloques para ver la vista previa.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Quiz Dialog */}
        <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crear Quiz</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateQuiz} className="space-y-4">
              <div>
                <Label>Nombre del quiz *</Label>
                <Input
                  value={quizForm.nombre}
                  onChange={(e) => setQuizForm({ ...quizForm, nombre: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={quizForm.descripcion}
                  onChange={(e) => setQuizForm({ ...quizForm, descripcion: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tiempo límite (min)</Label>
                  <Input
                    type="number"
                    value={quizForm.tiempo_limite || ''}
                    onChange={(e) => setQuizForm({ ...quizForm, tiempo_limite: parseInt(e.target.value) || null })}
                    placeholder="Sin límite"
                  />
                </div>
                <div>
                  <Label>Intentos permitidos</Label>
                  <Input
                    type="number"
                    value={quizForm.intentos_permitidos}
                    onChange={(e) => setQuizForm({ ...quizForm, intentos_permitidos: parseInt(e.target.value) || 1 })}
                    min="1"
                  />
                </div>
              </div>
              <div>
                <Label>Puntuación mínima para aprobar (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={quizForm.puntuacion_minima}
                  onChange={(e) => setQuizForm({ ...quizForm, puntuacion_minima: parseInt(e.target.value) || 60 })}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="mostrar-respuestas"
                  checked={quizForm.mostrar_respuestas}
                  onCheckedChange={(checked) => setQuizForm({ ...quizForm, mostrar_respuestas: checked })}
                />
                <Label htmlFor="mostrar-respuestas">Mostrar respuestas correctas al finalizar</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowQuizDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]">Crear y editar preguntas</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Enroll Student Dialog */}
        <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Añadir Alumno al Curso</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Seleccionar Alumno</Label>
                <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un alumno..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers
                      .filter(u => !enrollments.find(e => e.student_id === u.id))
                      .map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.nombre} {user.apellidos} ({user.email})
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {allUsers.filter(u => !enrollments.find(e => e.student_id === u.id)).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Todos los alumnos ya están inscritos o no hay alumnos registrados.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEnrollDialog(false)}>Cancelar</Button>
                <Button onClick={handleEnrollStudent} className="bg-[#004E89] hover:bg-[#003B66]">
                  Inscribir Alumno
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Add Tutor Dialog */}
        <Dialog open={showTutorDialog} onOpenChange={setShowTutorDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Añadir Tutor al Curso</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Los tutores pueden añadir contenido, crear quiz e interactuar con los alumnos de este curso.
              </p>
              <div>
                <Label>Seleccionar Instructor</Label>
                <Select value={selectedTutorId} onValueChange={setSelectedTutorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un instructor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors
                      .filter(i => !(course?.tutor_ids || []).includes(i.id))
                      .map(instructor => (
                        <SelectItem key={instructor.id} value={instructor.id}>
                          {instructor.nombre} {instructor.apellidos} ({instructor.email})
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {instructors.filter(i => !(course?.tutor_ids || []).includes(i.id)).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No hay instructores disponibles. Asegúrate de que existan usuarios con rol "Instructor".
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowTutorDialog(false)}>Cancelar</Button>
                <Button onClick={handleAddTutor} className="bg-blue-600 hover:bg-blue-700">
                  Añadir Tutor
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default OnlineCourseDetail;
